import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { Placement } from '@atlaskit/popper/main';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { AgentProfileCardWithTriggerProps } from '@atlassian/agent-profile-card/card-with-trigger';
import { AgentCard } from '@atlassian/agent-profile-card/card';
import { UserProfileCardForMention } from '@atlassian/user-profile-card/mention';

import { expVal, expValEquals } from '@confluence/feature-experiments';
import {
	type ProfileCardGetActions,
	getProfileCardProvider,
} from '@confluence/fabric-media-support/entry-points/ProfileCardProvider.ts';
import type { LoadableType } from '@confluence/loadable';
import { LoadableLazy } from '@confluence/loadable';
import { FeedFollow } from '@confluence/profile/entry-points/FeedFollow.ts';
import { useFeedFollow } from '@confluence/profile/entry-points/useFeedFollow.ts';
import { getProfileClient } from '@confluence/profile/entry-points/getProfileClient.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useGetNavigateToTeamsApp } from '@confluence/teams-app-config/entry-points/useGetNavigateToTeamsApp.ts';
import type { ProfileCardProvider } from '@confluence/fabric-media-support/entry-points/types.ts';

const LazyAgentProfileCardWithTrigger: LoadableType<AgentProfileCardWithTriggerProps> =
	LoadableLazy({
		loader: async () =>
			(
				await import(
					/* webpackChunkName: "loadable-AgentProfileCardWithTrigger" */
					'@atlassian/agent-profile-card/card-with-trigger'
				)
			).AgentProfileCardWithTrigger,
		loading: ({ children }) => <>{children}</>,
	});

type UseProfileCardProviderProps = {
	push: (url: string, forceReload?: boolean | undefined) => void;
	userId: string | null;
	cloudId: string;
	/**
	 * @deprecated This is no longer used - we should clean it up in a future update
	 */
	featureFlags?: Record<string, string | boolean>;
};

export function useProfilecardProvider({
	userId,
	cloudId,
	featureFlags,
}: UseProfileCardProviderProps): Promise<ProfileCardProvider> {
	const intl = useIntl();
	const feedFollowData = useFeedFollow();
	const getNavigateToTeamsApp = useGetNavigateToTeamsApp();
	const { orgId, userId: requestingUserId } = useSessionData();

	const handleProfileCardActions: (...args: any) => ProfileCardGetActions = useCallback(
		(loggedInUserId) => (profileCardUserId) => {
			const teamsAppPersonProfileUrl = getNavigateToTeamsApp({
				type: 'USER',
				payload: {
					userId: profileCardUserId,
				},
			}).href;

			const followed = !!(
				profileCardUserId && feedFollowData?.followedUsers?.includes(profileCardUserId)
			);
			return [
				{
					label: intl.formatMessage({ id: 'view.profile' }),
					callback: undefined,
					target: '_blank',
					link: teamsAppPersonProfileUrl,
				},
				{
					label: <FeedFollow followed={followed} />,
					id: 'feed-follow-user',
					callback: feedFollowData.getToggleFollowUser(profileCardUserId, followed, 'editor'),
					shouldRender: (userData?: { status: string }) =>
						feedFollowData.renderFeedFollow &&
						Boolean(profileCardUserId) &&
						profileCardUserId !== loggedInUserId &&
						userData?.status === 'active',
				},
			];
		},
		[feedFollowData, getNavigateToTeamsApp, intl],
	);

	return useMemo(() => {
		const providerPromise = getProfileCardProvider(
			cloudId,
			handleProfileCardActions(userId),
			featureFlags ?? {},
		);

		const shouldRenderUserMentionCard =
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			fg('people-teams_migrate-user-profile-card') ||
			expValEquals('pt_user_profile_card_migration_exp', 'isEnabled', true);
		const shouldRenderAgentMentionCard = isExperimentEnabled(
			'platform_editor_agent_profile_card_update',
		);

		if (!shouldRenderUserMentionCard && !shouldRenderAgentMentionCard) {
			return providerPromise;
		}

		return providerPromise.then((provider) => ({
			...provider,
			...(shouldRenderUserMentionCard && {
				renderUserMentionCard: ({
					userId: mentionUserId,
					cloudId: mentionCloudId,
					children,
					prefilledProfileData,
					referenceElement,
					placement,
					isReduced,
				}: {
					userId: string;
					cloudId: string;
					children: React.ReactNode;
					prefilledProfileData?: { accountId: string; name: string };
					referenceElement?: HTMLElement;
					placement?: Placement;
					isReduced?: boolean;
				}) => (
					<UserProfileCardForMention
						userId={mentionUserId}
						cloudId={mentionCloudId}
						orgId={orgId ?? ''}
						viewerId={requestingUserId ?? ''}
						referenceElement={referenceElement}
						prefilledProfileData={
							expVal('cc_comments_accessible_mention_profilecard_names', 'isEnabled', false)
								? prefilledProfileData
								: undefined
						}
						placement={placement}
						isReduced={isReduced}
						agent={{ resourceClient: getProfileClient(mentionCloudId, featureFlags ?? {}) }}
					>
						{children}
					</UserProfileCardForMention>
				),
			}),
			...(shouldRenderAgentMentionCard && {
				renderAgentMentionCard: ({
					accountId,
					children,
					cloudId: mentionCloudId,
					isReadOnly,
					onChatClick,
				}: {
					accountId: string;
					children?: React.ReactNode;
					cloudId: string;
					isReadOnly?: boolean;
					onChatClick?: (event: React.MouseEvent, agentStudioId?: string) => void;
				}) =>
					children ? (
						<LazyAgentProfileCardWithTrigger
							identityAccountId={accountId}
							cloudId={mentionCloudId}
							trigger="click"
							hideConversationStarters={isReadOnly}
							hideStarButton={isReadOnly}
							onChatClick={onChatClick}
						>
							{children}
						</LazyAgentProfileCardWithTrigger>
					) : (
						<AgentCard
							accountId={accountId}
							cloudId={mentionCloudId}
							resourceClient={getProfileClient(mentionCloudId, featureFlags ?? {})}
							hideConversationStarters={isReadOnly}
							hideStarButton={isReadOnly}
							onChatClick={onChatClick}
						/>
					),
			}),
		}));
	}, [cloudId, handleProfileCardActions, userId, featureFlags, orgId, requestingUserId]);
}
